import { Flex } from '@chakra-ui/react';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { match } from 'ts-pattern';
import { Creator } from '~/clientApi/creator.model';
import {
  EmptyState,
  LoadingState,
  MetricsContainer,
  getMetricsSource,
  getPlatformMetrics,
} from '~/components/CreatorChannelMetrics';
import { useFetchCreatorBenchmarks } from '~/hooks/api/useGetCreatorBenchmarks';
import { useGetCreatorProfile } from '~/hooks/api/useGetCreatorProfile';
import { useTalentManagerAccess } from '~/hooks/useTalentManagerAccess';
import { SupportedPlatforms } from '~/screens/CreatorAudienceDemographics';
import { selectTalentManager } from '~/selectors/talent-manager';
import { useCurrentUserAccess } from '~/userManagementApi/userAccess';
import { MeResponse } from '~/userManagementApi/userAccess.types';

interface Props {
  platform: SupportedPlatforms;
  metrics?: string[];
}

export const CreatorChannelMetrics: React.FC<Props> = ({
  platform,
  metrics = [],
}) => {
  const router = useRouter();
  const slug = router.query.creatorSlug as string;

  const manager = useSelector(selectTalentManager);

  const { data: creator } = useGetCreatorProfile({ slug });

  const benchmarks = useFetchCreatorBenchmarks({
    creatorIds: [creator?.id as string],
    managerId: creator?.manager?.id as string,
  });

  const creatorPlatformMetrics = useMemo(() => {
    if (!benchmarks.isSuccess) return;

    return getPlatformMetrics(benchmarks.data?.data || []);
  }, [benchmarks.data?.data, benchmarks.isSuccess]);

  const platformMetricsSource = useMemo(() => {
    if (!benchmarks.isSuccess) return;

    return getMetricsSource(benchmarks.data?.data || []);
  }, [benchmarks.data?.data, benchmarks.isSuccess]);

  const userAccessData = useCurrentUserAccess({
    entityId: manager?.agency?.id,
  });

  const { shouldHaveAccessAsManagerComanagerAdmin } = useTalentManagerAccess(
    creator as Creator,
    userAccessData.data as MeResponse,
  );

  return (
    <Flex
      flexDirection="column"
      gap={4}
      justify-content={{ base: 'center', md: 'flex-start' }}
    >
      {match(benchmarks.status)
        .with('loading', () => <LoadingState times={4} />)
        .with('error', () => {
          return (
            <EmptyState
              isManager={shouldHaveAccessAsManagerComanagerAdmin}
              platform={platform}
              hasError={true}
            />
          );
        })
        .with('success', () => (
          <>
            <MetricsContainer
              platform={platform}
              creatorPlatformMetrics={creatorPlatformMetrics}
              platformMetricsSource={platformMetricsSource}
              isBuilder={false}
              selectedMetrics={metrics}
            />
          </>
        ))
        .otherwise(() => (
          <EmptyState
            platform={platform}
            hasError={benchmarks.isError}
            isManager={shouldHaveAccessAsManagerComanagerAdmin}
          />
        ))}
    </Flex>
  );
};
